import dynamic from 'next/dynamic'
import React, { ReactNode, useEffect, useState } from 'react'

import { hjEvent } from '../utils/hotjar'
import { checkIfUserSubscriptionIsFree } from '../utils/pricing'
import { TFrontendLimitType, TLimitType } from '../utils/types'
import { CommonModalsLightProvider } from './CommonModalsLightContext'
import { useCurrentUser } from './CurrentUserContext'
import { useInternationalization } from './InternationalizationContext'

type TPricingLimit = TLimitType | TFrontendLimitType | null

const CancelSequenceModal = dynamic(() =>
  import('../components/settings/CancelSequenceContainer/CancelSequenceModal').then(
    (module) => module.CancelSequenceModal,
  ),
)
const WelcomeModal = dynamic(() => import('../components/signup/welcome/WelcomeModal'))
const PricingModal = dynamic(() => import('../components/signup/pricing/PricingModal'))

type FetchType = {
  // Welcome
  welcomeOpen: boolean
  setWelcomeOpen: (v: boolean) => void
  // Pricing
  pricingOpen: boolean
  setPricingOpen: (v: boolean) => void
  pricingTrialScreenShown: boolean
  setPricingTrialScreenShown: (v: boolean) => void
  pricingResumeScreenShown: boolean
  setPricingResumeScreenShown: (v: boolean) => void
  pricingLimitType: TPricingLimit
  setPricingLimitType: (v: TPricingLimit) => void
  handleOpenPricing: (open: boolean, limit?: TPricingLimit) => void
  //
  feedbackIsOpen: boolean
  setFeedbackIsOpen: (a?: boolean) => void
  cancelSequenceIsOpen: boolean
  setCancelSequenceIsOpen: (a?: boolean) => void
  launchpadOpen: boolean
  setLaunchpadOpen: (a: boolean) => void
}

export const CommonModalsContext = React.createContext<FetchType>({
  feedbackIsOpen: false,
  setFeedbackIsOpen: () => {},
  cancelSequenceIsOpen: false,
  setCancelSequenceIsOpen: () => {},
  launchpadOpen: false,
  setLaunchpadOpen: () => {},
  welcomeOpen: false,
  setWelcomeOpen: () => {},
  pricingOpen: false,
  setPricingOpen: () => {},
  pricingTrialScreenShown: false,
  setPricingTrialScreenShown: () => {},
  pricingResumeScreenShown: false,
  setPricingResumeScreenShown: () => {},
  pricingLimitType: null,
  setPricingLimitType: () => {},
  handleOpenPricing: () => {},
})

export const CommonModalsProvider = ({ children }: { children: ReactNode }) => {
  const { addComponentText } = useInternationalization()
  const { currentUser } = useCurrentUser()

  // Welcome
  const [welcomeOpen, setWelcomeOpen] = useState(false)
  // Pricing
  const [pricingOpen, setPricingOpen] = useState(false)
  const [pricingLimitType, setPricingLimitType] = useState<TPricingLimit>(null)
  const [pricingTrialScreenShown, setPricingTrialScreenShown] = useState(false)
  const [pricingResumeScreenShown, setPricingResumeScreenShown] = useState(false)
  //
  const [launchpadOpen, setLaunchpadOpen] = useState<boolean>(false)
  const [feedbackIsOpen, setFeedbackIsOpen] = useState(false)
  const [cancelSequenceIsOpen, setCancelSequenceIsOpen] = useState(false)

  useEffect(() => {
    import('../components/signup/welcome/WelcomeModal')
    import('../components/signup/pricing/PricingModal')
  }, [])
  useEffect(() => {
    addComponentText('CommonModalsContext')
  }, [])
  useEffect(() => {
    if (currentUser && (!currentUser.prefs?.quizTaken || !currentUser.prefs?.afterRegModalShown)) {
      hjEvent('event', 'quiz_open')
      setWelcomeOpen(true)
    }
  }, [])

  const handleOpenPricing = (open: boolean, limit?: TPricingLimit) => {
    const isUserSubscriptionFree = checkIfUserSubscriptionIsFree(currentUser)
    const numerousLimits: TPricingLimit[] = [
      'projects',
      'preview_shares',
      'song_sections',
      'song_section_chords',
      'song_track_layers',

      'ai_prompt_generations',
      'ai_generations',
      'ai_chord_suggestion',
      'ai_lyrics',

      'chord_voicings',
    ]

    setPricingOpen(open)

    if (currentUser?.isSubscriptionPastDue && limit) {
      setPricingResumeScreenShown(true)
      return
    }

    if (isUserSubscriptionFree && (!limit || !numerousLimits.includes(limit))) {
      setPricingTrialScreenShown(!!limit)
    } else {
      setPricingLimitType(limit || null)
    }
  }

  return (
    <CommonModalsContext.Provider
      value={{
        feedbackIsOpen,
        setFeedbackIsOpen: (val) => setFeedbackIsOpen(!!val),
        cancelSequenceIsOpen,
        setCancelSequenceIsOpen: (val) => setCancelSequenceIsOpen(!!val),
        launchpadOpen,
        setLaunchpadOpen,
        welcomeOpen,
        setWelcomeOpen,
        handleOpenPricing,
        pricingOpen,
        setPricingOpen,
        pricingTrialScreenShown,
        setPricingTrialScreenShown,
        pricingResumeScreenShown,
        setPricingResumeScreenShown,
        pricingLimitType,
        setPricingLimitType,
      }}
    >
      <CommonModalsLightProvider>
        <WelcomeModal
          opened={welcomeOpen}
          onClose={() => {
            setWelcomeOpen(false)

            if (!currentUser?.isSubscribed) {
              setTimeout(() => {
                setPricingOpen(true)
                setPricingLimitType(null)
                setPricingTrialScreenShown(true)
                setPricingResumeScreenShown(false)
              }, 300)
            }
          }}
        />
        <PricingModal
          opened={pricingOpen}
          limitType={pricingLimitType}
          onClose={() => {
            setPricingOpen(false)
            setPricingLimitType(null)
            setPricingTrialScreenShown(false)
            setPricingResumeScreenShown(false)
          }}
        />
        <CancelSequenceModal
          isOpen={cancelSequenceIsOpen}
          onClose={() => {
            setCancelSequenceIsOpen(false)
          }}
        />
        {children}
      </CommonModalsLightProvider>
    </CommonModalsContext.Provider>
  )
}

export const useCommonModals = () => React.useContext<FetchType>(CommonModalsContext)
